/* Store product gallery, quantity controls and source-backed information tabs. */
if (!customElements.get('store-product-gallery')) customElements.define('store-product-gallery', class extends HTMLElement {
  connectedCallback() {
    this.controller?.abort(); this.controller = new AbortController();
    const options = {signal: this.controller.signal};
    this.track = this.querySelector('.store-gallery__slides');
    this.slides = [...this.querySelectorAll('[data-store-slide]')];
    this.thumbnails = [...this.querySelectorAll('[data-gallery-thumbnail]')];
    this.counter = this.querySelector('[data-gallery-counter]');
    this.index = Math.max(0, this.slides.findIndex(slide => slide.dataset.mediaId === this.dataset.initialMedia));
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.querySelector('.store-gallery__controls').hidden = this.slides.length < 2;
    const thumbnailStrip = this.querySelector('.store-gallery__thumbnails');
    if (thumbnailStrip) thumbnailStrip.hidden = this.slides.length < 2;
    this.querySelector('[data-gallery-previous]').addEventListener('click', () => this.move(-1), options);
    this.querySelector('[data-gallery-next]').addEventListener('click', () => this.move(1), options);
    this.thumbnails.forEach((button, index) => button.addEventListener('click', () => this.go(index), options));
    this.track.addEventListener('keydown', event => {
      let next;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') next = Math.max(0, Math.min(this.slides.length - 1, this.index + window.TytoDirection.arrow(event.key, this)));
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = this.slides.length - 1;
      if (next === undefined || !this.slides.length) return;
      event.preventDefault(); this.go(next);
    }, options);
    this.track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        if (!this.slides.length) return;
        if (this.pendingIndex !== null && this.pendingIndex !== undefined) {
          const destination = window.TytoDirection.target(this.track, this.slides[this.pendingIndex]);
          if (Math.abs(this.track.scrollLeft - destination) < 2) { this.sync(this.pendingIndex); this.pendingIndex = null; }
          return;
        }
        const nearest = window.TytoDirection.nearest(this.track, this.slides);
        this.sync(nearest);
      });
    }, {...options, passive: true});
    this.track.addEventListener('pointerdown', () => { this.pendingIndex = null; }, options);
    this.track.addEventListener('wheel', () => { this.pendingIndex = null; }, {...options, passive:true});
    this.sizeObserver?.disconnect();
    this.sizeObserver = new ResizeObserver(() => this.go(this.index, 'auto'));
    this.sizeObserver.observe(this.track);
    const root = this.closest('[data-product-section]');
    const variantSelect = root?.querySelector('select[name="id"]');
    if (variantSelect) variantSelect.addEventListener('change', () => {
      const option = variantSelect.selectedOptions[0];
      if (!option) return;
      const price = root.querySelector('[data-product-price]');
      if (price) {
        price.replaceChildren(document.createTextNode(option.dataset.price || ''));
        if (option.dataset.comparePrice) { const previous = document.createElement('s'); previous.textContent = option.dataset.comparePrice; price.append(previous); }
      }
      const submit = root.querySelector('[name="add"]');
      if (submit) { submit.disabled = option.dataset.available !== 'true'; submit.textContent = submit.disabled ? window.TytoDirection.text('sold_out') : window.TytoDirection.text('add_to_cart'); }
      const mediaIndex = this.slides.findIndex(slide => slide.dataset.mediaId === option.dataset.mediaId);
      if (mediaIndex >= 0) this.go(mediaIndex);
      const url = new URL(location.href); url.searchParams.set('variant', option.value); history.replaceState({}, '', url);
    }, options);
    this.go(this.index, 'auto'); this.dataset.ready = 'true';
  }
  move(direction) {
    if (this.slides.length < 2) return;
    const next = Math.max(0, Math.min(this.slides.length - 1, this.index + direction));
    this.go(next);
  }
  go(index, behavior) {
    if (!this.slides.length) return;
    this.pendingIndex = index;
    this.sync(index);
    this.track.scrollTo({left: window.TytoDirection.target(this.track, this.slides[index]), behavior: behavior || (this.motion.matches ? 'auto' : 'smooth')});
    if (Math.abs(this.track.scrollLeft - (window.TytoDirection.target(this.track, this.slides[index]))) < 2) this.pendingIndex = null;
  }
  sync(index) {
    if (!this.slides[index]) return;
    const changed = this.index !== index;
    this.index = index;
    this.querySelector('[data-gallery-previous]').disabled = index === 0;
    this.querySelector('[data-gallery-next]').disabled = index === this.slides.length - 1;
    this.slides.forEach((slide, i) => {
      slide.inert = i !== index;
      slide.setAttribute('aria-hidden', String(i !== index));
      if (changed && i !== index) {
        slide.querySelectorAll('video,model-viewer').forEach(media => media.pause?.());
        slide.querySelectorAll('iframe').forEach(frame => {
          const origin = new URL(frame.src, location.href).origin;
          if (/youtube(?:-nocookie)?\.com$/.test(new URL(origin).hostname)) frame.contentWindow?.postMessage(JSON.stringify({event:'command',func:'pauseVideo',args:[]}), origin);
          if (new URL(origin).hostname === 'player.vimeo.com') frame.contentWindow?.postMessage({method:'pause'}, origin);
        });
      }
    });
    this.thumbnails.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
    if (this.counter) this.counter.textContent = `${index + 1} / ${this.slides.length}`;
    const active = this.thumbnails[index];
    if (active) { const strip = active.parentElement; strip.scrollTo({left: window.TytoDirection.target(strip, active, 'center'), behavior:'auto'}); }
  }
  disconnectedCallback() { this.controller?.abort(); this.sizeObserver?.disconnect(); cancelAnimationFrame(this.frame); }
});

if (!customElements.get('store-product-quantity')) customElements.define('store-product-quantity', class extends HTMLElement {
  connectedCallback() {
    this.controller?.abort(); this.controller = new AbortController();
    const options = {signal: this.controller.signal}, input = this.querySelector('input'), decrease = this.querySelector('[data-quantity-decrease]'), increase = this.querySelector('[data-quantity-increase]');
    const sync = () => { const value = Number(input.value); decrease.disabled = value <= Number(input.min || 1); increase.disabled = Boolean(input.max) && value >= Number(input.max); };
    const change = direction => { if (direction > 0) input.stepUp(); else input.stepDown(); input.dispatchEvent(new Event('input',{bubbles:true})); input.dispatchEvent(new Event('change',{bubbles:true})); sync(); };
    decrease.addEventListener('click', () => change(-1), options); increase.addEventListener('click', () => change(1), options);
    input.addEventListener('input', sync, options); input.addEventListener('change', sync, options); sync();
  }
  disconnectedCallback() { this.controller?.abort(); }
});

if (!customElements.get('store-product-information')) customElements.define('store-product-information', class extends HTMLElement {
  connectedCallback() {
    this.controller?.abort(); this.controller = new AbortController();
    const options = {signal:this.controller.signal};
    this.tabs = [...this.querySelectorAll('.store-product-tabs__navigation > [role="tab"]')]; this.panels = [...this.querySelectorAll(':scope > .store-product-tabs__panel')];
    this.querySelector('[role="tablist"]').hidden = false;
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index), options);
      tab.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') next = (index + window.TytoDirection.arrow(event.key, this) + this.tabs.length) % this.tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = this.tabs.length - 1;
        if (next === undefined) return;
        event.preventDefault(); this.select(next); this.tabs[next].focus({preventScroll:true});
      }, options);
    });
    this.select(0); this.dataset.ready = 'true';
  }
  select(index) { this.tabs.forEach((tab,i) => {tab.setAttribute('aria-selected',String(i===index));tab.tabIndex=i===index?0:-1;}); this.panels.forEach((panel,i) => panel.hidden=i!==index); }
  disconnectedCallback() { this.controller?.abort(); }
});
